import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
	Chip,
	Tabs,
	Tab,
	Tooltip,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import {
	API_TIME_FORMAT,
	DISPLAY_DATE_FORMAT,
	DISPLAY_DATE_MINUTE_FORMAT,
} from '@/utils/dateHelper';
import dayjs from 'dayjs';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import MeditationListSummaryFilterSection from './components/InterventionWorkerSummaryFilterSection';
import { CaseFilter, CasePageDTO } from '@/api/DTO/case-management/caseDto';
import {
	useGetMyInterventionListQuery,
	useGetMyProgramListQuery,
} from '@/api/case-management/caseApi';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { CaseNoteStatusEnum, PermissionEnum } from '@/api/enum/common.enum';
import EditCaseNoteDialog from '../EditCaseNoteDialog';
import { useGetUser } from '@/hooks/useGetUser';
export default function InterventionWorkerSummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const navigate = useNavigate();
	const [currentTab, setCurrentTab] = useState('worker');
	const [open, setOpen] = useState(false);
	const [openCurrentId, setOpenCurrentId] = useState('');
	const { userInfo } = useGetUser();

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
		if (newValue == 'worker') navigate(`/case-management/intervention`);
		if (newValue == 'submitter')
			navigate(`/case-management/intervention/submitter`);
	};

	const defaultPropertyFilter: CaseFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
		castNoteStatusList: [],
		additionalList: [],
		natureList: [],
	};
	const defaultValues: DefaultValues<CaseFilter> = defaultPropertyFilter;
	const methods = useForm<CaseFilter>({
		defaultValues,
	});
	const transformedSearchParams: CaseFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'castNoteStatusList',
			'additionalList',
			'natureList',
		]),
	};
	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetMyInterventionListQuery(
			{
				...transformedSearchParams,
				type: 0,
				dateRangeStart: methods.getValues('dateRangeStart'),
				dateRangeEnd: methods.getValues('dateRangeEnd'),
			},
			{
				refetchOnMountOrArgChange: true,
			}
		);

	const transformResponse = useCallback((response?: any[]) => {
		if (response == null) return;
		const newResponse: any[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				interventionDate: !item.interventionDate
					? '-'
					: dayjs(item.interventionDate)
							.locale('en')
							.format(DISPLAY_DATE_FORMAT),
				interventionStartTime: !item.interventionStartTime
					? '-'
					: dayjs(item.interventionStartTime, API_TIME_FORMAT)
							.locale('en')
							.format(DISPLAY_DATE_MINUTE_FORMAT),
				interventionEndTime: !item.interventionEndTime
					? '-'
					: dayjs(item.interventionEndTime, API_TIME_FORMAT)
							.locale('en')
							.format(DISPLAY_DATE_MINUTE_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as CaseFilter;

			const cleanedFilter = Object.fromEntries(
				Object.entries(propertyFilterFormResult).filter(([key, val]) => {
					return val !== '' && val != null;
				})
			);

			// append to url to reload page
			setSearchParams(qs.stringify(cleanedFilter));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const columns: GridColDef[] = [
		{
			field: 'fileNo',
			headerName: t('member.fileNo'),
			flex: 15,
		},
		{
			field: 'nameEn',
			headerName: t('member.name'),
			flex: 15,
			renderCell: (params) => (
				<Box>
					<Typography variant='body2'>{params.row.nameEn}</Typography>
					<Typography variant='body2'>{params.row.nameZh}</Typography>
				</Box>
			),
		},
		{
			field: 'interventionDate',
			headerName: t('case.date'),
			flex: 15,
		},
		{
			field: 'interventionStartTime',
			headerName: t('case.startTime'),
			flex: 15,
		},
		{
			field: 'interventionEndTime',
			headerName: t('case.endTime'),
			flex: 15,
		},
		{
			field: 'natureDesc',
			headerName: t('case.nature'),
			flex: 15,
		},

		{
			field: 'additionalDesc',
			headerName: t('case.additional'),
			flex: 15,
		},
		{
			field: 'riskLevelDesc',
			headerName: t('case.riskLevel'),
			flex: 15,
		},

		{
			field: 'familyInvolvedAlliedHealthServicesEscortDesc',
			headerName: t('case.familyInvolvedAlliedHealthServicesEscort'),
			flex: 15,
		},
		{
			field: 'supportingStaffDesc',
			headerName: t('case.supportingStaff'),
			flex: 15,
		},
		{
			field: 'interventionWorkerName',
			headerName: t('case.inWorker'),
			flex: 15,
		},
		{
			field: 'submittedByName',
			headerName: t('case.submittedBy'),
			flex: 15,
		},
		{
			field: 'caseNoteStatusDesc',
			headerName: t('case.caseNoteStatus'),
			flex: 15,
		},
		{
			field: 'edit',
			headerName: t('case.updateCaseNote'),

			width: 68,
			renderCell: (params) =>
				userInfo?.permissionList?.includes(
					PermissionEnum.CaseManagementEditCaseNote
				) &&
				params.row.caseNoteStatus != CaseNoteStatusEnum.Submitted && (
					<IconButton
						color='secondary'
						onClick={() => {
							setOpenCurrentId(params.row.id);
							setOpen(true);
						}}
					>
						<EditOutlinedIcon />
					</IconButton>
				),
		},
		{
			field: 'deteil',
			headerName: '',

			width: 68,
			renderCell: (params) => (
				<Link to={`/case-management/intervention/detail/${params.id}`}>
					<IconButton color='secondary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
	];

	return (
		<>
			<EditCaseNoteDialog
				open={open}
				setOpen={setOpen}
				openCurrentId={openCurrentId}
			/>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{t('drawerListItems.myInterventionSummary')}
				</Typography>
				{userInfo?.permissionList?.includes(
					PermissionEnum.CaseManagementCreateIntervention
				) && (
					<Button
						endIcon={<AddCircleOutlineOutlinedIcon />}
						onClick={() => {
							navigate(`/case-management/intervention/create/my-intervention`);
						}}
						variant='contained'
						sx={{ marginRight: '20px' }}
					>
						{t('button.newIntervention')}
					</Button>
				)}
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					{' '}
					<Tabs
						value={currentTab}
						onChange={handleTabChange}
						sx={{ marginBottom: 3 }}
					>
						<Tab
							label={
								<Typography variant='body1'>
									{t('case.interventionWorker')}
								</Typography>
							}
							value='worker'
							sx={{ textTransform: 'none' }}
						/>
						<Tab
							label={
								<Typography variant='body1'>{t('case.submitter')}</Typography>
							}
							value='submitter'
							sx={{ textTransform: 'none' }}
						/>
					</Tabs>
					<FormProvider {...methods}>
						<MeditationListSummaryFilterSection></MeditationListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
